/**
 * @fileoverview Data helpers: field property lookups, search-request/filter
 * formation, request/response mapping, dropdown lookups, and sorting utilities.
 * @exports Field, request/filter mapping, lookup, and sorting helpers
 * @author {Cognizant Technology Solutions}
 */
import collabReportFieldMapping from '../constants/fieldMappings/collabReportFieldMapping';
import fieldMapping from '../constants/fieldMappings/poSearchFieldMapping';
import messageText from '../constants/messageText';
import ppmReportFieldMapping from '../constants/fieldMappings/ppmReportFieldMapping';
import ppmReportFieldProperties from '../constants/fieldProperties/ppmReportFieldProperties';
import lineSummaryReportFieldProperties from '../constants/fieldProperties/lineSummaryReportFieldProperties';
import collabReportFieldProperties from '../constants/fieldProperties/collabReportFieldProperties';
import poSearchFieldProperties from '../constants/fieldProperties/poSearchFieldProperties';
import gacEventErrorTableFieldProperties from '../constants/fieldProperties/gacEventErrorTableFieldProperties';
import gacEventTableFieldProperties from '../constants/fieldProperties/gacEventTableFieldProperties';
import gacUpdateTableFieldProperties from '../constants/fieldProperties/gacUpdateTableFieldProperties';
import myRequestTableFieldProperties from '../constants/fieldProperties/myRequestTableFieldProperties';
import poChangeReportFieldProperties from '../constants/fieldProperties/poChangeReportFieldProperties';
import poEventErrorTableFieldProperties from '../constants/fieldProperties/poEventErrorTableFieldProperties';
import sireProfileTableFieldProperties from '../constants/fieldProperties/sireProfileTableFieldProperties';
import sireSummaryTableFieldProperties from '../constants/fieldProperties/sireSummaryTableFieldProperties';
import wipTableFieldProperties from '../constants/fieldProperties/wipTableFieldProperties';
import poChangeReportFieldMapping from '../constants/fieldMappings/poChangeReportFieldMapping';
import conversationThreadsFieldProperties from '../constants/fieldProperties/conversationThreadsFieldProperties';
import conversationThreadReportFieldProperties from '../constants/fieldProperties/conversationThreadReportFieldProperties';
import pmoDecFieldProperties from '../constants/fieldProperties/pmoDecFieldProperties';
import operationalMetricsFieldProperties from '../constants/fieldProperties/operationalmetricsFieldProperties';
import geoGlobalFieldProperties from '../constants/fieldProperties/geoGlobalFieldProperties';
import geoGlobalFieldMapping from '../constants/fieldMappings/geoGlobalFieldMapping';
import lineSummaryReportFieldMapping from '../constants/fieldMappings/lineSummaryReportFieldMapping';
import airFreightReportFieldProperties from '../constants/fieldProperties/airFreightReportFieldProperties';
import gacChangeHistoryReportFieldProperties from '../constants/fieldProperties/gacChangeHistoryReportFieldProperties';
import conversationCategoryCodesFieldProperties from '../constants/fieldProperties/conversationCategoryCodesFieldProperties';
import conversationThreadReportFieldMapping from '../constants/fieldMappings/conversationThreadReportFieldMapping';
import gacReasonCodeFieldProperties from '../constants/fieldProperties/gacReasonCodeFieldProperties';
import gacReasonUsecasesFieldProperties from '../constants/fieldProperties/gacReasonUsecasesFieldProperties';
import geoEmailGroupFieldProperties from '../constants/fieldProperties/geoEmailGroupFieldProperties';
import featureFlagFieldProperties from '../constants/fieldProperties/featureFlagFieldProperties';
import airFreightReportFieldMapping from '../constants/fieldMappings/airFreightReportFieldMapping';
import { showForMultiSelect } from './envServer';
import gacChangeHistoryReportFieldMapping from '../constants/fieldMappings/gacChangeHistoryReportFieldMapping';

const propertiesMappingCollection = {
  poSearchFieldProperties,
  collabReportFieldProperties,
  ppmReportFieldProperties,
  lineSummaryReportFieldProperties,
  sireProfileTableFieldProperties,
  poChangeReportFieldProperties,
  gacEventErrorTableFieldProperties,
  gacEventTableFieldProperties,
  gacUpdateTableFieldProperties,
  myRequestTableFieldProperties,
  poEventErrorTableFieldProperties,
  sireSummaryTableFieldProperties,
  wipTableFieldProperties,
  conversationThreadsFieldProperties,
  conversationThreadReportFieldProperties,
  operationalMetricsFieldProperties,
  pmoDecFieldProperties,
  geoGlobalFieldProperties,
  geoEmailGroupFieldProperties,
  airFreightReportFieldProperties,
  conversationCategoryCodesFieldProperties,
  gacReasonCodeFieldProperties,
  gacChangeHistoryReportFieldProperties,
  gacReasonUsecasesFieldProperties,
  featureFlagFieldProperties
};

/**
 * The Method Updates the valid date formts
 * @param {number} date getting the date value from collab objects
 * @returns {string} valid date format to display
 *
 */
export const getDateInYYYYMMDD = (selectedDate: Object) => {
  // $FlowIssue
  const seletedDate = new Date(selectedDate);
  const month = (seletedDate.getMonth() + 1).toString().padStart(2, '0');
  const day = seletedDate.getDate().toString().padStart(2, '0');
  const fullYear = seletedDate.getFullYear();
  return `${fullYear}-${month}-${day}`;
};

/**
 * This method is used for getting the field properties with
 * the field key for looking up the POFileds array.
 * returns the object from the table header array.
 * @param {string} field for which the object to be returned
 * @returns {Object} fieldProperties for the corresponding key
 */
export const getFieldProperties = (field: string, type?: string) => {
  if (type) {
    const currentPropertiesMapping = propertiesMappingCollection[`${type}FieldProperties`] || {};
    return currentPropertiesMapping[field] || {};
  }
  const matchedPropertyMappingKey = Object.keys(propertiesMappingCollection)
    .find((collectionKey) => propertiesMappingCollection[collectionKey][field]);
  return propertiesMappingCollection[matchedPropertyMappingKey]?.[field] || {};
};

/**
 * field fieldTemplate for request, selects a predified list of operators
 * that would be selected by setting the fieldName using name (setter)
 * @returns {
 *  fieldName,
 *  operator,
 *  fieldValue,
 *  name: setter -> fieldName && operator,
 *  value: setter -> fieldValue
 * }
 * @flow
 */
export const getFieldTemplate = () => {
  const getFieldMap = (fieldName: string) => {
    switch (fieldName) {
      case airFreightReportFieldMapping.AIR_FREIGHT_PLANT_CODE:
      case conversationThreadReportFieldMapping.CONVO_THREAD_CONVO_TOPIC_CODE:
      case 'collaborationTopic.code':
      case ppmReportFieldMapping.PPM_REPORT_GAC_REASON_CODE:
      case lineSummaryReportFieldMapping.LINE_SUMMARY_GAC_REASON_CODE:
      case ppmReportFieldMapping.PPM_REPORT_DIVISION_CODE:
      case ppmReportFieldMapping.PPM_REPORT_PURCHASE_ORG_CODE:
      case poChangeReportFieldMapping.PO_CHANGE_DOC_TYPE_CODE:
      case poChangeReportFieldMapping.PO_CHANGE_VENDOR_CODE:
      case poChangeReportFieldMapping.PO_CHANGE_CHANGED_TYPE:
      case poChangeReportFieldMapping.PO_CHANGE_SEASON_YEAR:
      case poChangeReportFieldMapping.PO_CHANGE_SEASON_CODE:
      case ppmReportFieldMapping.PPM_REPORT_DOC_TYPE_CODE:
      case collabReportFieldMapping.REPORT_VENDOR_CODE:
      case collabReportFieldMapping.REPORT_PO_DOC_TYPE:
      case fieldMapping.PMO_DEC_CODE:
      case ppmReportFieldMapping.PPM_REPORT_SEASON_CODE:
      case ppmReportFieldMapping.PPM_REPORT_SEASON_YEAR:
      case fieldMapping.DIVISION:
      case fieldMapping.YEAR:
      case fieldMapping.SEASON:
      case fieldMapping.PURCHASE_ORG_CODE:
      case fieldMapping.PURCHASING_DOCUMENT_TYPE_CODE:
      case fieldMapping.POSEARCH_FIELDS_OF_PLAY_NAME:
      case ppmReportFieldMapping.PPM_REPORT_FIELDS_OF_PLAY_NAME:
      case lineSummaryReportFieldMapping.LINE_SUMMARY_FIELDS_OF_PLAY_NAME:
      case ppmReportFieldMapping.PPM_REPORT_BIS_CLASSIFICATION:
      case lineSummaryReportFieldMapping.LINE_SUMMARY_BIS_CLASSIFICATION:
      case geoGlobalFieldMapping.GEO_GLOBAL_DOCTYPE:
      case fieldMapping.POSEARCH_ASIAN_CC:
      case fieldMapping.VENDOR_CODE:
      case fieldMapping.POSEARCH_GOODS_SUPPLIER_CODE:
      case fieldMapping.MANUFACTURING_CODE:
      case gacChangeHistoryReportFieldMapping.GAC_CHANGE_HISTORY_SUB_TIER2_VENDOR_CODE: {
        return {
          operator: '=',
          getFieldValue: (data: Array<Object>) => data.map((each) => each.id || each)
        };
      }
      case collabReportFieldMapping.REPORT_DATE_APPROVED_OR_REJECTED:
      case collabReportFieldMapping.REPORT_REQUEST_INITIATED_DATE:
      case collabReportFieldMapping.REPORT_DOCUMENT_DATE:
      case collabReportFieldMapping.REPORT_CREATED_DATE:
      case ppmReportFieldMapping.PPM_REPORT_DOC_DATE:
      case ppmReportFieldMapping.PPM_REPORT_CHANGED_DATE:
      case ppmReportFieldMapping.PPM_REPORT_MRGAC:
      case ppmReportFieldMapping.PPM_REPORT_OGAC:
      case ppmReportFieldMapping.PPM_REPORT_GAC:
      case ppmReportFieldMapping.PPM_REPORT_ORIGIN_RECEIPT_ACTUAL_DATE:
      case ppmReportFieldMapping.PPM_REPORT_CREATED_DATE:
      case ppmReportFieldMapping.PPM_REPORT_ACCEPT_PO_ACTUAL_DATE:
      case ppmReportFieldMapping.PPM_REPORT_FACTORY_DELIVERY_ACTUAL_DATE:
      case ppmReportFieldMapping.PPM_REPORT_CURRENT_EVENT_DATE:
      case ppmReportFieldMapping.PPM_REPORT_NEXT_EVENT_DATE:
      case poChangeReportFieldMapping.PO_CHANGE_CHANGE_DATE:
      case poChangeReportFieldMapping.PO_CHANGE_DOCUMENT_DATE:
      case poChangeReportFieldMapping.PO_CHANGE_GAC_DATE:
      case poChangeReportFieldMapping.PO_CHANGE_OGAC_DATE:
      case fieldMapping.CREATE_DATE:
      case fieldMapping.DOCUMENT_DATE:
      case fieldMapping.OGAC:
      case fieldMapping.GAC:
      case fieldMapping.MRGAC:
      case fieldMapping.PO_ACCEPTANCE_DATE: {
        return { operator: '>=' };
      }
      default: {
        return { operator: '=' };
      }
    }
  };

  return {
    getFieldMap,
    fieldName: '',
    operator: '',
    fieldValue: null,
    set name(fieldName) {
      this.fieldName = fieldName;
      const fieldMap = getFieldMap(fieldName);
      this.operator = fieldMap.operator;
    },
    set value(data) {
      const fieldMap = getFieldMap(this.fieldName);
      this.fieldValue = fieldMap.getFieldValue ? fieldMap.getFieldValue(data) : data;
    }
  };
};

/**
 * function to convert the input fields data into contract format
 * for the date range fields, two fieldTemplate will be created for from and to
 * @param {Object} inputData
 * @returns {Array<Object>} in contract request format
 */

export function getRequestForDynamicRequest(
  inputData: Object,
  fieldKey: string,
  searchRequestData: Object
) {
  if (inputData[fieldKey].staticRange) {
    const fieldTemplate = getFieldTemplate();
    fieldTemplate.name = fieldKey; // e.g 'poHeader.partnerNumber'
    fieldTemplate.type = 'DYNAMIC';
    fieldTemplate.operator = inputData[fieldKey].staticRange;
    delete fieldTemplate.fieldValue;
    searchRequestData.push(fieldTemplate);
  } else {
    if (inputData[fieldKey].startFromDays || inputData[fieldKey].startFromDays === 0) {
      const fieldTemplate = getFieldTemplate();
      fieldTemplate.name = fieldKey; // e.g 'poHeader.partnerNumber'
      fieldTemplate.type = 'DYNAMIC';
      fieldTemplate.operator = '>=';
      fieldTemplate.fieldValue = inputData[fieldKey].startFromDays.toString();
      searchRequestData.push(fieldTemplate);
    }
    if (inputData[fieldKey].endFromDays || inputData[fieldKey].endFromDays === 0) {
      const toFieldTemplate = getFieldTemplate();
      toFieldTemplate.name = fieldKey; // e.g 'poHeader.partnerNumber'
      toFieldTemplate.type = 'DYNAMIC';
      toFieldTemplate.operator = '<=';
      toFieldTemplate.fieldValue = inputData[fieldKey].endFromDays.toString();
      searchRequestData.push(toFieldTemplate);
    }
  }
}
// True when the "to" date value for a field is a valid date.
function checkConditionForToDate(inputData: Object, fieldKey: string) {
  return inputData[fieldKey].to
    && (
      typeof inputData[fieldKey].to === 'object'
      || typeof inputData[fieldKey].to === 'string'
    )
    && !Number.isNaN(new Date(inputData[fieldKey].to).getDate());
}

// True when the "from" date value for a field is a valid date.
function checkConditionForFromDate(inputData: Object, fieldKey: string) {
  return inputData[fieldKey].from
    && (
      typeof inputData[fieldKey].from === 'object'
      || typeof inputData[fieldKey].from === 'string'
    )
    && !Number.isNaN(new Date(inputData[fieldKey].from).getDate());
}
/**
 * Appends date-range (from/to) or dynamic-date entries to the search request
 * for a date field, formatting datetime fields with day boundaries.
 * @param {Object} inputData The input field values
 * @param {string} fieldKey The date field key
 * @param {Object} searchRequestData The request array to append to
 * @param {Object} fieldProperties The field's properties (type, requestDataType)
 * @returns {void}
 */
export function dateRangeRequestFormation(
  inputData: Object,
  fieldKey: string,
  searchRequestData: Object,
  fieldProperties: Object
) {
  if ((fieldProperties.requestDataType === 'dynamicDate')
    && inputData[fieldKey]
    && typeof inputData[fieldKey] === 'object'
    && Object.keys(inputData[fieldKey]).length > 0
    && inputData[fieldKey].isDynamic) {
    getRequestForDynamicRequest(inputData, fieldKey, searchRequestData);
  } else {
    if (checkConditionForFromDate(inputData, fieldKey)) {
      const dateFrom = new Date(inputData[fieldKey].from);
      const dateFromTemplate = getFieldTemplate();
      dateFromTemplate.name = fieldKey;
      dateFromTemplate.value = (fieldProperties.type === 'datetime') ? `${dateFrom.getFullYear()}-${(dateFrom.getMonth() + 1).toString()
        .padStart(2, '0')}-${dateFrom.getDate().toString().padStart(2, '0')}T00:00:00.000Z`
        : `${dateFrom.getFullYear()}-${(dateFrom.getMonth() + 1).toString()
          .padStart(2, '0')}-${dateFrom.getDate().toString().padStart(2, '0')}`;
      dateFromTemplate.operator = '>=';
      searchRequestData.push(dateFromTemplate);
    }
    if (checkConditionForToDate(inputData, fieldKey)) {
      const dateTo = new Date(inputData[fieldKey].to);
      const dateToTemplate = getFieldTemplate();
      dateToTemplate.name = fieldKey; // e.g 'poHeader.partnerNumber'
      dateToTemplate.value = fieldProperties.type === 'datetime' ? `${dateTo.getFullYear()}-${(dateTo.getMonth() + 1).toString().padStart(2, '0')}-${dateTo.getDate().toString().padStart(2, '0')}T23:59:59.000Z`
        : `${dateTo.getFullYear()}-${(dateTo.getMonth() + 1).toString().padStart(2, '0')}-${dateTo.getDate().toString().padStart(2, '0')}`;
      dateToTemplate.operator = '<=';
      searchRequestData.push(dateToTemplate);
    }
  }
}
const MULTI_SELECT_MAPPING_TYPES = new Set(['poSearch', 'ppmReport', 'lineSummaryReport']);

const AIR_FREIGHT_STATUS_FIELDS = new Set([
  airFreightReportFieldMapping.AIR_FREIGHT_DPOM_LINE_ITEM_STATUS,
  airFreightReportFieldMapping.AIR_FREIGHT_REQUEST_STATUS
]);

// True when the mapping type supports multi-select fields.
function isMultiSelectReportMapping(mappingType) {
  return MULTI_SELECT_MAPPING_TYPES.has(mappingType);
}

// True when a value is a non-empty string or non-empty object.
function hasNonEmptyFieldValue(value) {
  return (typeof value === 'string' && value)
    || (value && typeof value === 'object' && Object.keys(value).length > 0);
}

// Appends an air-freight status field (mapped to fieldValue ids); returns
// whether it handled the field.
function appendAirFreightStatusField(inputData, fieldKey, searchRequestData) {
  if (!AIR_FREIGHT_STATUS_FIELDS.has(fieldKey)) {
    return false;
  }
  if (!Array.isArray(inputData[fieldKey]) || inputData[fieldKey].length === 0) {
    return false;
  }
  const fieldTemplate = getFieldTemplate();
  fieldTemplate.name = fieldKey;
  fieldTemplate.fieldValue = inputData[fieldKey].map((each) => each.id || each);
  fieldTemplate.operator = '=';
  searchRequestData.push(fieldTemplate);
  return true;
}

// Appends a comma-separated PO line item status field; returns whether handled.
function appendPoLineItemStatusField(inputData, fieldKey, searchRequestData) {
  if (fieldKey !== fieldMapping.PO_LINE_ITEM_STATUS || !inputData[fieldKey].includes(',')) {
    return false;
  }
  const fieldTemplate = getFieldTemplate();
  fieldTemplate.name = fieldKey;
  fieldTemplate.value = inputData[fieldKey].split(',');
  searchRequestData.push(fieldTemplate);
  return true;
}

// Appends a multi-select geography code field; returns whether handled.
function appendGeographyCodeField(inputData, fieldKey, mappingType, searchRequestData) {
  if (fieldKey !== fieldMapping.GEOGRAPHY_CODE
    || inputData[fieldKey].length === 0
    || !showForMultiSelect
    || !isMultiSelectReportMapping(mappingType)) {
    return false;
  }
  let value = inputData[fieldKey];
  if (typeof value === 'string') {
    value = [value];
  }
  const fieldTemplate = getFieldTemplate();
  fieldTemplate.name = fieldKey;
  fieldTemplate.value = value.map((each) => each.id || each);
  fieldTemplate.operator = '=';
  searchRequestData.push(fieldTemplate);
  return true;
}

// Appends a multi-select product code field; returns whether handled.
function appendProductCodeField(inputData, fieldKey, mappingType, searchRequestData) {
  if (fieldKey !== fieldMapping.PRODUCT_CODE
    || !showForMultiSelect
    || !isMultiSelectReportMapping(mappingType)) {
    return false;
  }
  if (typeof inputData[fieldKey] === 'string' && inputData[fieldKey]) {
    const fieldTemplate = getFieldTemplate();
    fieldTemplate.name = fieldKey;
    fieldTemplate.value = [inputData[fieldKey]];
    searchRequestData.push(fieldTemplate);
    return true;
  }
  if (inputData[fieldKey]
    && typeof inputData[fieldKey] === 'object'
    && Object.keys(inputData[fieldKey]).length > 0) {
    const fieldTemplate = getFieldTemplate();
    fieldTemplate.name = fieldKey;
    fieldTemplate.value = inputData[fieldKey];
    searchRequestData.push(fieldTemplate);
    return true;
  }
  return false;
}

// Appends a plain non-date field with a non-empty value; returns whether handled.
function appendDefaultInputField(inputData, fieldKey, fieldProperties, searchRequestData) {
  if (fieldProperties.type === 'date' || fieldProperties.type === 'datetime') {
    return false;
  }
  if (!hasNonEmptyFieldValue(inputData[fieldKey])) {
    return false;
  }
  const fieldTemplate = getFieldTemplate();
  fieldTemplate.name = fieldKey;
  fieldTemplate.value = inputData[fieldKey];
  searchRequestData.push(fieldTemplate);
  return true;
}

/**
 * Appends a single input field to the search request, dispatching to the
 * appropriate field-type builder (status, geography, product, date, default).
 * @param {Object} inputData The input field values
 * @param {string} fieldKey The field key to append
 * @param {string} mappingType The active mapping type
 * @param {Object} fieldProperties The field's properties
 * @param {Object} searchRequestData The request array to append to
 * @returns {void}
 */
function appendInputFieldRequest(
  inputData: Object,
  fieldKey: string,
  mappingType: string,
  fieldProperties: Object,
  searchRequestData: Object
) {
  if (appendAirFreightStatusField(inputData, fieldKey, searchRequestData)) {
    return;
  }
  if (appendPoLineItemStatusField(inputData, fieldKey, searchRequestData)) {
    return;
  }
  if (appendGeographyCodeField(inputData, fieldKey, mappingType, searchRequestData)) {
    return;
  }
  if (appendProductCodeField(inputData, fieldKey, mappingType, searchRequestData)) {
    return;
  }
  if (fieldProperties.type === 'date' || fieldProperties.type === 'datetime') {
    dateRangeRequestFormation(inputData, fieldKey, searchRequestData, fieldProperties);
    return;
  }
  appendDefaultInputField(inputData, fieldKey, fieldProperties, searchRequestData);
}

/**
 * Maps all input field values into the contract search-request format.
 * @param {Object} inputData The input field values
 * @param {string} mappingType The active mapping type
 * @returns {Array<Object>} The search request entries
 */
export const mapInputFieldsToRequest = (inputData: Object, mappingType: string) => {
  const searchRequestData = [];
  Object.keys(inputData).forEach((fieldKey: string) => {
    const fieldProperties = getFieldProperties(fieldKey, mappingType);
    appendInputFieldRequest(inputData, fieldKey, mappingType, fieldProperties, searchRequestData);
  });

  return searchRequestData;
};

/**
 * Appends a string or multi-select filter value to the request, extracting
 * the primary token (and coercing numeric fields) for piped values.
 * @param {Object} inputData The input field values
 * @param {string} fieldKey The field key
 * @param {Object} searchRequestData The request array to append to
 * @param {string} type The mapping type used for field property lookup
 * @returns {void}
 */
export function dateTimeRequestData(inputData, fieldKey, searchRequestData, type) {
  if (typeof inputData[fieldKey] === 'string' && inputData[fieldKey]) {
    const fieldTemplate = getFieldTemplate();
    fieldTemplate.name = fieldKey; // e.g 'productCode'
    fieldTemplate.fieldValue = inputData[fieldKey];
    fieldTemplate.operator = '=';
    searchRequestData.push(fieldTemplate);
  } else if ((typeof inputData[fieldKey] === 'object'
    && Object.keys(inputData[fieldKey]).length > 0)) {
    const fieldTemplate = getFieldTemplate();
    fieldTemplate.name = fieldKey; // e.g categoryCode'
    fieldTemplate.fieldValue = inputData[fieldKey].map((filterValue) => {
      const indexOfPipe = filterValue.toString().indexOf('|');
      const poFieldProperty = getFieldProperties(fieldKey, type);
      if (indexOfPipe > -1) {
        const primaryFilterValue = filterValue.substr(0, indexOfPipe).trim();
        const primaryValueAsNumber = Number(primaryFilterValue);
        if (poFieldProperty?.type === 'number' && !Number.isNaN(primaryValueAsNumber)) {
          return primaryValueAsNumber;
        }
        return primaryFilterValue;
      }
      return filterValue;
    });
    fieldTemplate.operator = '=';
    searchRequestData.push(fieldTemplate);
  }
}

/**
 * Maps filter input values into the request format, handling date fields as
 * ranges and other fields as value entries.
 * @param {Object} inputData The filter input values
 * @param {string} mappingType The active mapping type
 * @param {string} [type] Optional field-property lookup type
 * @returns {Array<Object>} The filter request entries
 */
export const mapFiltersWithDatesToRequest = (
  inputData: Object, mappingType: string, type: string = undefined
) => {
  const searchRequestData = [];
  Object.keys(inputData).forEach((fieldKey: string) => {
    const fieldProperties = getFieldProperties(fieldKey, mappingType);
    if (!(fieldProperties.type === 'date' || fieldProperties.type === 'datetime')) {
      dateTimeRequestData(inputData, fieldKey, searchRequestData, type);
    } else {
      dateRangeRequestFormation(inputData, fieldKey, searchRequestData, fieldProperties);
    }
  });

  return searchRequestData;
};

/**
 * converts selected fitlers data from the store to
 * contract request format
 * @param {Object} selectedFilters - filters Object from the store
 * @returns {Array<Object>} in contract request format
 * Used for filter property of the request
 */
export const mapFiltersToRequest = (selectedFilters: Object = {}, type: string = undefined) => {
  const filtersRequestData = [];
  Object.keys(selectedFilters).forEach((filter: string) => {
    if (selectedFilters[filter]
      && typeof selectedFilters[filter] === 'object'
      && Object.keys(selectedFilters[filter]).length > 0) {
      const fieldTemplate = getFieldTemplate();
      fieldTemplate.fieldName = filter; // e.g 'poHeader.partnerNumber'
      fieldTemplate.fieldValue = selectedFilters[filter].map((filterValue) => {
        const indexOfPipe = filterValue.toString().indexOf('|');
        const poFieldProperty = getFieldProperties(filter, type);
        if (indexOfPipe > -1) {
          let primaryFilterValue = filterValue.substr(0, indexOfPipe).trim();
          if (fieldTemplate.fieldName === 'product.consumerConstructDimensionName') {
            primaryFilterValue = filterValue.substr(indexOfPipe + 1).trim();
          }
          const primaryValueAsNumber = Number(primaryFilterValue);
          if (poFieldProperty?.type === 'number' && !Number.isNaN(primaryValueAsNumber)) {
            return primaryValueAsNumber;
          }
          return primaryFilterValue;
        }
        return filterValue;
      });
      fieldTemplate.operator = '=';
      filtersRequestData.push(fieldTemplate);
    }
  });

  return filtersRequestData;
};

/**
 * Maps a field's array of values into `{ id }` option objects.
 * @param {Object} field The request field with a `fieldValue` array
 * @returns {Array<Object>} The `{ id }` value objects
 */
function mapToFieldValue(field) {
  return field.fieldValue.map
    ? field.fieldValue.map((value) => ({ id: value }))
    : [];
}

/**
 * Builds the dynamic-date field value (static range or start/end from-days)
 * from a request field.
 * @param {string} field The request field descriptor
 * @param {Object} inputFieldsData The accumulated input field values
 * @returns {Object} The updated input field values
 */
export function getDynamicFieldValue(field: string, inputFieldsData: Object) {
  if (!field.fieldValue) {
    return {
      ...inputFieldsData,
      [field.fieldName]: {
        isDynamic: true,
        staticRange: field.operator
      }
    };
  }
  const fieldData = {
    ...inputFieldsData[field.fieldName],
    isDynamic: true,
    [(field.operator === '>=') ? 'startFromDays' : 'endFromDays']: field.fieldValue
  };
  return {
    ...inputFieldsData,
    [field.fieldName]: fieldData
  };
}

const MAP_TO_FIELD_VALUE_FIELD_NAMES = new Set([
  ppmReportFieldMapping.PPM_REPORT_VENDOR,
  ppmReportFieldMapping.PPM_REPORT_GAC_REASON_CODE,
  lineSummaryReportFieldMapping.LINE_SUMMARY_GAC_REASON_CODE,
  ppmReportFieldMapping.PPM_REPORT_PMO_DEC_CODE,
  collabReportFieldMapping.REPORT_VENDOR_CODE,
  fieldMapping.VENDOR_CODE,
  fieldMapping.POSEARCH_GOODS_SUPPLIER_CODE,
  fieldMapping.MANUFACTURING_CODE,
  fieldMapping.PMO_DEC_CODE,
  collabReportFieldMapping.REPORT_PO_DOC_TYPE,
  ppmReportFieldMapping.PPM_REPORT_DOC_TYPE_CODE,
  poChangeReportFieldMapping.PO_CHANGE_DOC_TYPE_CODE,
  poChangeReportFieldMapping.PO_CHANGE_VENDOR_CODE,
  poChangeReportFieldMapping.PO_CHANGE_CHANGED_TYPE,
  poChangeReportFieldMapping.PO_CHANGE_SEASON_YEAR,
  poChangeReportFieldMapping.PO_CHANGE_SEASON_CODE,
  fieldMapping.DIVISION,
  fieldMapping.YEAR,
  fieldMapping.SEASON,
  fieldMapping.PURCHASE_ORG_CODE,
  fieldMapping.PURCHASING_DOCUMENT_TYPE_CODE,
  fieldMapping.POSEARCH_FIELDS_OF_PLAY_NAME,
  ppmReportFieldMapping.PPM_REPORT_FIELDS_OF_PLAY_NAME,
  lineSummaryReportFieldMapping.LINE_SUMMARY_FIELDS_OF_PLAY_NAME,
  lineSummaryReportFieldMapping.LINE_SUMMARY_BIS_CLASSIFICATION,
  ppmReportFieldMapping.PPM_REPORT_BIS_CLASSIFICATION,
  geoGlobalFieldMapping.GEO_GLOBAL_DOCTYPE,
  fieldMapping.POSEARCH_ASIAN_CC
]);

// Updates a date field's from/to bound from a request field's operator/value.
function getDateFieldUpdate(field, existingFieldData) {
  const fieldData = existingFieldData || { from: null, to: null };
  const dateOnly = field.fieldValue?.includes('T')
    ? field.fieldValue.split('T')[0]
    : field.fieldValue || '';
  if (field.operator === '>=' || field.operator === '>') {
    return {
      ...fieldData,
      from: new Date(`${dateOnly}T00:00:00.000`)
    };
  }
  return {
    ...fieldData,
    to: new Date(`${dateOnly}T23:59:59.999`)
  };
}

/**
 * Converts a saved-search request body back into the UI input field format.
 * @param {Object} poSearchRequest The request body search entries
 * @returns {Object} The input field values the UI understands
 */
export const mapRequestToInputFields = (poSearchRequest: Object) => {
  const inputFieldsData = {};
  poSearchRequest.forEach((field) => {
    const fieldProperty = getFieldProperties(field.fieldName);
    if (MAP_TO_FIELD_VALUE_FIELD_NAMES.has(field.fieldName)) {
      inputFieldsData[field.fieldName] = mapToFieldValue(field);
    } else if (
      field.fieldName === fieldMapping.PO_LINE_ITEM_STATUS && Array.isArray(field.fieldValue)
    ) {
      inputFieldsData[field.fieldName] = field.fieldValue.join(',');
    } else if (field.type === 'DYNAMIC') {
      getDynamicFieldValue(field, inputFieldsData);
    } else if (fieldProperty.type === 'date' || fieldProperty.type === 'datetime') {
      inputFieldsData[field.fieldName] = getDateFieldUpdate(
        field,
        inputFieldsData[field.fieldName]
      );
    } else if (field.fieldName === fieldMapping.DELETION_INDICATOR) {
      // this is intentional
      // noop
    } else {
      inputFieldsData[field.fieldName] = field.fieldValue;
    }
  });
  return inputFieldsData;
};

/**
 * Traverses the response object with the field key for getting unique
 * values of the data rows.
 * @param {Array<Object>} filtersFromResponse - the filters property in the
 * contract response received.
 * @returns {Object} in the format for consumtions of fitler inputs in UI
 */
export const getFilterValuesFromResponse = (filtersFromResponse: Array<Object> = []) => {
  const filterValues = {};
  filtersFromResponse.forEach((filter) => {
    filterValues[filter.filterName] = filter.filterValues.map((each) => {
      let filterValue = each.primary;
      if (each.secondary) {
        if (filter.filterName === 'product.consumerConstructDimensionName') {
          filterValue = `${each.secondary} | ${each.primary}`;
        } else {
          filterValue += ` | ${each.secondary}`;
        }
      }
      return filterValue;
    }).filter(Boolean);
  });
  return filterValues;
};

/**
 * Returns the filters in response for the request body after converting
 * from the filters reducer format
 * @param {Object} selectedFilters in the format of filters reducer
 * @param {string} type for the mapping type of the current flow
 * @returns {Array<Object>} of the filtersInResponse request body
 */
export const getFiltersInResponseRequestBody = (
  selectedFilters: Object = {}, type: string = 'poSearch'
) => {
  const currentPropertiesMapping = propertiesMappingCollection[`${type}FieldProperties`] || {};
  return Object.keys(selectedFilters)
    .filter((filter) => selectedFilters[filter])
    .map((filter) => {
      const filterInResponse = {};
      const fieldProperties = currentPropertiesMapping[filter] || {};
      if (!fieldProperties.primary) {
        return null;
      }
      filterInResponse.primary = fieldProperties.primary;
      if (fieldProperties.secondary) {
        filterInResponse.secondary = fieldProperties.secondary;
      }
      return filterInResponse;
    })
    .filter(Boolean);
};

/**
 * Builds the SIRE request body from selected PO result rows.
 * @param {Array<Object>} poDataRows The PO result rows
 * @param {Array<string>} ids The selected row size ids
 * @returns {Object} The SIRE request body with `poData` entries
 */
export const getSireRequestBodyFromPOResults = (
  poDataRows: Array<Object> = [],
  ids: Array<string> = []
) => {
  const selectedRows = poDataRows.filter((row) => ids.includes(row[fieldMapping.SIZE_ID]));

  return {
    poData: selectedRows.map((row) => ({
      poNumber: row[fieldMapping.PURCHASE_ORDER_NUMBER],
      itemNumber: row[fieldMapping.PO_LINE_ITEM_NUMBER],
      scheduleLineItemNumber: row[fieldMapping.SCHEDULE_LINE_ITEM_NUMBER]
    }))
  };
};

/**
 * This function can be used for sorting an array of objects.
 * This supports Array of primitive types also, yet it supports only
 * flattened objects for now.
 * @param {Array<Object>} data to be sorted
 * @param {string} sortBy optional parameter propertyName with which the data should be sorted
 * @param {boolean} isOrderDesc whether the order should be descending
 */
export const sortData = (
  data: Array<Object> = [], sortBy?: string = '', isOrderDesc: boolean = false
) => {
  const comparer = (firstElement, secondElement) => {
    let firstElementValue = firstElement;
    let secondElementValue = secondElement;
    if (sortBy) {
      firstElementValue = firstElement[sortBy];
      secondElementValue = secondElement[sortBy];
    }

    if (isOrderDesc) {
      return (secondElementValue > firstElementValue) ? 1 : -1;
    }
    return (secondElementValue > firstElementValue) ? -1 : 1;
  };
  return data.sort(comparer);
};

/**
 * This methods helps you to validate the domain of email id
 * It bascically checks if its valid nike mail id
 * returns the boolean value.
 * @param {string} emailId which needs to be validated
 * @returns {boolean} based on the validation
 */

export const validateStakeHolderEmailDomain = (emailId: string = '') => {
  let validated = false;
  const emailSubStrings = emailId.split('@');
  if (emailSubStrings.length === 2 && (emailSubStrings[1].toLowerCase() === 'nike.com')) {
    validated = true;
  }
  return validated;
};

/**
 * This methods helps you to validate email id
 * returns the boolean value.
 * @param {string} emailId which needs to be validated
 * @returns {boolean} based on the validation
 */

export const validateStakeHolderEmail = (emailId: string) => {
  let emailValidation = false;
  const emailRegx = /^[-!#$%&'*+0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/;
  if (emailRegx.test(String(emailId).toLowerCase())) {
    emailValidation = true;
  }
  return emailValidation;
};

/**
 * This method is used to get the Id from the session storage
 * and to remove the Id from session storage
 */
export const readSessionStorage = (key: string) => {
  const value = sessionStorage.getItem(key);
  sessionStorage.removeItem(key);
  return value;
};

/**
 * This method is used to validate if the pathName exists
 * And to validate if validateId is a string
 */
export const validatePathName = (pathName: string, validateId: string) => {
  const requiredPathNames = ['conversationthreads', 'posearch', 'poreport', 'ppmreport', 'pochangereport', 'lineitemreport', 'conversationreport', 'airfreightreport', 'gacchangehistoryreport'];
  const hasPath = requiredPathNames.find((name) => pathName.includes(name));
  return hasPath && typeof validateId === 'string';
};

/**
 * This action is used for building the request for default stakeholders api
 * @param {Array<string>} ids the array of selected po number ids
 * @returns {Object} request body for default stake holders api
 */
export const getDefaultStakeHoldersRequest = (
  poDataRows: Array<Object>, type: string, ids: Array<string> = []
) => {
  const selectedRows = (ids.length > 0)
    ? poDataRows.filter((row) => {
      if (type === 'excludes') {
        return !ids.includes(row[fieldMapping.SIZE_ID]);
      }
      return ids.includes(row[fieldMapping.SIZE_ID]);
    })
    : poDataRows;

  return {
    poData: selectedRows.map((row) => ({
      id: row.id,
      [fieldMapping.PURCHASE_ORDER_NUMBER]: row[fieldMapping.PURCHASE_ORDER_NUMBER],
      [fieldMapping.PO_LINE_ITEM_NUMBER]: row[fieldMapping.PO_LINE_ITEM_NUMBER],
      [fieldMapping.SCHEDULE_LINE_ITEM_NUMBER]: row[fieldMapping.SCHEDULE_LINE_ITEM_NUMBER]
    }))
  };
};

/**
 * Builds a success message from a template, interpolating counts and pluralizing
 * "line"/"lines" and "is"/"are" based on the values.
 * @param {string} key The message text key
 * @param {number|string} firstValue The primary count (e.g. total lines)
 * @param {number|string} secondValue The secondary count (e.g. validated)
 * @returns {string} The interpolated success text
 */
export const getDynamicSuccessText = (
  key: string,
  firstValue: number | string,
  secondValue: number | string
) => {
  if (!messageText[key]) {
    return '';
  }
  let dynamicText = messageText[key];
  const text1 = 'line';
  const text2 = 'is';
  const text3 = 'lines';
  const text4 = 'are';
  const totalLength = (firstValue != null)
    ? firstValue.toString()
    : '';
  const validatedLength = (secondValue != null)
    ? secondValue.toString()
    : '';
  dynamicText = dynamicText.replace('{{value1}}', totalLength);
  dynamicText = dynamicText.replace('{{value2}}', validatedLength);
  if (!firstValue || firstValue <= 1) {
    dynamicText = dynamicText.replace('{{lines}}', text1);
  } else {
    dynamicText = dynamicText.replace('{{lines}}', text3);
  }
  if (!secondValue || secondValue <= 1) {
    dynamicText = dynamicText.replace('{{are}}', text2);
  } else {
    dynamicText = dynamicText.replace('{{are}}', text4);
  }
  return dynamicText;
};

/**
 * This function is used to get the conversation thread search query string
 * to be supplied to get request
 * @param {Object} inputData from the conversation search panel
 * @returns {String} the search query to be passed in the get request
 */
export const getConversationThreadSearchRequest = (inputData: Object) => {
  const sortParam = '&sort=lastConversationDate';
  let searchQuery = '';

  const collaborationTopicCodeQuery = (key) => {
    const collaborationTopic = inputData[key]?.map
      ? inputData[key].map(({ id }) => id)
      : [];
    if (collaborationTopic.length > 0) {
      searchQuery += `&filter=${key}(${collaborationTopic.join()})`;
    }
  };

  const vendorCodesQuery = (key) => {
    const vendorCodes = inputData[key]?.map
      ? inputData[key].map(({ id }) => id)
      : [];
    if (vendorCodes.length > 0) {
      searchQuery += `&filter=${key}(${vendorCodes.join()})`;
    }
  };

  Object.keys(inputData).forEach((key) => {
    switch (key) {
      case 'isStakeholder': {
        const isStakeholderValue = inputData[key] ? 'Y' : 'N';
        searchQuery += `&filter=${key}(${isStakeholderValue})`;
        break;
      }
      case 'lastConversationDate': {
        const { from, to } = inputData[key];
        if (from) {
          const fromDate = getDateInYYYYMMDD(from);
          searchQuery += `&filter=${key}After(${fromDate}T00:00:00.000Z)`;
        }
        if (to) {
          const toDate = getDateInYYYYMMDD(to);
          searchQuery += `&filter=${key}Before(${toDate}T23:59:59.999Z)`;
        }
        break;
      }
      case 'vendorCodes': {
        vendorCodesQuery(key);
        break;
      }
      case 'collaborationTopic.code': {
        collaborationTopicCodeQuery(key);
        break;
      }
      default: {
        if (inputData[key].length > 0) {
          searchQuery += `&filter=${key}(${inputData[key]})`;
        }
        break;
      }
    }
  });

  return `${searchQuery}${sortParam}`;
};

/**
 * This function is used to get the conversation thread search query string
 * to be supplied to get request
 * @param {Object} inputData from the conversation search panel
 * @returns {String} the search query to be passed in the get request
 */
export const getAFAdjustmentSearchRequest = (inputData: Object) => {
  let searchQuery = '';

  const pmoQuery = (key) => {
    const vendorCodes = inputData[key]?.map
      ? inputData[key].map(({ id }) => id)
      : [];
    if (vendorCodes.length > 0) {
      searchQuery += `${key}(${vendorCodes.join()}),`;
    }
  };

  Object.keys(inputData).forEach((key) => {
    switch (key) {
      case 'createdDate': {
        const { from, to } = inputData[key];
        if (from) {
          const fromDate = getDateInYYYYMMDD(from);
          searchQuery += `${key}After(${fromDate}T00:00:00.000Z),`;
        }
        if (to) {
          const toDate = getDateInYYYYMMDD(to);
          searchQuery += `${key}Before(${toDate}T23:59:59.999Z),`;
        }
        break;
      }
      case 'productionNikeLiaisonOffice': {
        pmoQuery(key);
        break;
      }
      default: {
        if (inputData[key].length > 0) {
          searchQuery += `${key}(${inputData[key]}),`;
        }
        break;
      }
    }
  });
  searchQuery = `&filter=${searchQuery.slice(0, -1)}`;
  return searchQuery;
};

/**
 * Returns the filter dropdown values from the flattened response
 * @param {Object} response from the api flattened
 * @param {*} filters array for which the unique drowpdown data do be extracted
 * @param {String} mapping type oft he mapping used
 * @returns {Object} of filter drowdown values
 */
export const getFilterDropdownValuesFromResponse = (
  response: Object,
  filters: Array<string>,
  mappingType: string
) => {
  const filterValues = {

  };
  filters.forEach((filter) => {
    const fieldProperties = getFieldProperties(filter, mappingType);
    const uniqueValues = {};
    const temp = {};
    response.forEach((row) => {
      if (row[filter]) {
        if (
          (mappingType === 'pmoDec' && filter === 'managingUserEmail')
          || (mappingType === 'geoGlobal' && filter === 'email')
          || (mappingType === 'geoEmailGroup' && filter === 'emailID')
        ) {
          temp[row[filter].toLowerCase()] = true;
        } else if (mappingType === 'geoEmailGroup' && filter === 'geoEmailList') {
          row[filter].forEach((geoEmailList) => {
            uniqueValues[geoEmailList.emailID.toLowerCase()] = true;
          });
        } else if (fieldProperties.secondary && row[fieldProperties.secondary]) {
          uniqueValues[`${row[filter]} | ${row[fieldProperties.secondary]}`] = true;
        } else {
          uniqueValues[row[filter]] = true;
        }
      }
    });
    if (
      (mappingType === 'pmoDec' && filter === 'managingUserEmail')
      || (mappingType === 'geoGlobal' && filter === 'email')
      || (mappingType === 'geoEmailGroup' && filter === 'emailID')
    ) {
      filterValues[filter] = Object.keys(temp);
    } else if (mappingType === 'gacReasonUsecases' && filter === 'isActive') {
      filterValues[filter] = ['Active', 'Inactive'];
    } else {
      filterValues[filter] = Object.keys(uniqueValues);
    }
  });
  return filterValues;
};

/**
 * Filters response rows to those matching all selected filter values, with
 * special handling for email and geo-email-list fields.
 * @param {Array<Object>} response The response rows
 * @param {Object} filterValues The selected filter values by key
 * @param {string} type The mapping type used for field property lookup
 * @returns {Array<Object>} The filtered rows
 */
export const getFilteredResults = (response: Array<Object>, filterValues: Object, type: string) => (
  response.filter((row) => (
    Object.keys(filterValues).every((key) => {
      const fieldProperties = getFieldProperties(key, type);
      const secondaryValue = (fieldProperties.secondary && row[fieldProperties.secondary])
        ? ` | ${row[fieldProperties.secondary] || ''}`
        : '';
      let result;
      if (row[key] !== undefined && row[key].length > 0 && filterValues[key].length > 0 && (
        (type === 'pmoDec' && key === 'managingUserEmail')
        || (type === 'geoGlobal' && key === 'email')
        || (type === 'geoEmailGroup' && key === 'emailID')
      )) {
        result = (
          !filterValues[key]
          || filterValues[key].length === 0
          || filterValues[key].includes(row[key].toLowerCase() + secondaryValue)
        );
      } else if ((type === 'geoEmailGroup') && row[key].length > 0 && filterValues[key].length > 0 && (key === 'geoEmailList')) {
        row[key].forEach((obj) => {
          if (obj.emailID !== undefined && filterValues[key].includes(obj.emailID.toLowerCase())) {
            result = true;
          }
        });
      } else {
        result = (
          !filterValues[key]
          || filterValues[key].length === 0
          || filterValues[key].includes(row[key] + secondaryValue)
        );
      }
      return result;
    })
  ))
);
/**
 * Finds the dropdown option matching a single selected value, returning it
 * with a cleaned display text (secondary token preferred).
 * @param {Object} selectedValue The selected value (or `{ value }` object)
 * @param {Array<Object>} dropdownValues The dropdown options
 * @param {Object} [options] Optional custom text/value property names
 * @returns {?Object} The matching option, or null
 */
export const lookupDropdownOptionByValue = (
  selectedValue: Object,
  dropdownValues: Array<object>,
  options?: Object
) => {
  if (!selectedValue || !dropdownValues) {
    return null;
  }
  let text = 'text';
  let value = 'value';

  if (options?.optionProps) {
    text = options.optionProps.text;
    value = options.optionProps.value;
  }
  let selectedValueCode = selectedValue;
  if (typeof selectedValue === 'object') {
    selectedValueCode = selectedValue[value];
  }

  const dropdownValue = dropdownValues.find((option) => option[value] === selectedValueCode) || {};
  const optionText = (dropdownValue[text] || '').split(' | ');
  const returnValue = { ...dropdownValue, [text]: optionText[1] || optionText[0] };
  return returnValue;
};

/**
 * Finds the dropdown options matching an array (or comma string) of selected
 * values by id.
 * @param {Array<Object>} selectedValue The selected values (array or string)
 * @param {Array<Object>} dropdownValues The dropdown options
 * @param {Object} [options] Optional custom id property name
 * @returns {Array<Object>} The matching options
 */
export const lookupDropdownOptionsByArrayValue = (
  selectedValue: Array<object>,
  dropdownValues: Array<object>,
  options?: Object
) => {
  if (!selectedValue || !dropdownValues) {
    return [];
  }
  let id = 'id';
  let returnValue = [];

  if (options?.optionProps) {
    id = options.optionProps.id;
  }

  if (Array.isArray(selectedValue) && selectedValue[0] !== undefined) {
    const selectedItems = new Set(selectedValue.map((item) => item[id] || item));
    returnValue = dropdownValues.filter((option) => selectedItems.has(option[id]));
  } else if (typeof selectedValue === 'string') {
    const selectedItems = new Set(selectedValue.split(',').map((item) => item.trim()));
    returnValue = dropdownValues.filter((option) => selectedItems.has(option[id]));
  }

  return returnValue;
};

/**
 * Returns whether saved column order options exist in state.
 * @param {Object} state The Redux state
 * @param {boolean} [isCalledFromSlice] Whether called from a slice selector
 * @returns {boolean} True when column order data is present
 */
export const checkForColumnData = (
  state: Object, isCalledFromSlice?: boolean
) => (
  isCalledFromSlice
    ? (state.searchpaneldata?.selectedColumnOrderOptions?.length > 0)
    : (state.SearchPanelData?.selectedColumnOrderOptions?.length > 0)
);

/**
 * Returns a copy of a filter map with each array value sorted alphabetically.
 * @param {Object} data The filter map
 * @returns {Object|string} The sorted filter map, or an error string
 */
export const sortedFilterData = (data: Object) => {
  if (typeof data !== 'object' || data === null) {
    return 'Invalid data format.';
  }
  const sortedData = {};
  Object.keys(data).forEach((key) => {
    if (Array.isArray(data[key]) && data[key].length > 0) {
      sortedData[key] = [...data[key]].sort((a, b) => {
        const valueA = String(a).toLowerCase();
        const valueB = String(b).toLowerCase();
        if (valueA < valueB) return -1;
        if (valueA > valueB) return 1;
        return 0;
      });
    } else {
      sortedData[key] = data[key];
    }
  });
  return sortedData;
};

/**
 * Sorts config filter arrays ascending, leaving plant/purchase group as-is.
 * @param {Object} data The config filter map
 * @returns {Object} The sorted config filter map
 */
export const sortDataAscendingConfig = (data: Object) => {
  const sortedData = {};
  Object.keys(data).forEach((key) => {
    if (key === 'plantCode') {
      sortedData[key] = data[key];
    } else if (key === 'purchaseGroup') {
      sortedData[key] = data[key];
    } else if (Array.isArray(data[key]) && data[key].length > 0) {
      sortedData[key] = data[key].toSorted((a, b) => {
        const valueA = a.toLowerCase();
        const valueB = b.toLowerCase();
        if (valueA < valueB) return -1;
        if (valueA > valueB) return 1;
        return 0;
      });
    } else {
      sortedData[key] = data[key];
    }
  });
  return sortedData;
};

/**
 * Returns the grouped-column PPM header definitions for the Asian C&C label.
 * @param {string} column The column identifier
 * @param {string} label The column label
 * @returns {Array<Object>} The grouped header definitions, or []
 */
export const getPPMHeadersForGroupedColumn = (column: string, label: string) => {
  if (column === 'groupedColumn' && label === 'Asian C&C Label') {
    return [
      ppmReportFieldProperties[ppmReportFieldMapping.PPM_REPORT_KOREA_CHEST_SIZE],
      ppmReportFieldProperties[ppmReportFieldMapping.PPM_REPORT_CHINA_TOP_SIZE]
    ];
  }
  return [];
};

/**
 * Returns the grouped-column PO Search header definitions for the Asian C&C
 * label.
 * @param {string} column The column identifier
 * @param {string} label The column label
 * @returns {Array<Object>} The grouped header definitions, or []
 */
export const getPOSearhHeadersForGroupedColumn = (column: string, label: string) => {
  if (column === 'groupedColumn' && label === 'Asian C&C Label') {
    return [
      poSearchFieldProperties[fieldMapping.POSEARCH_KOREA_CHEST_SIZE],
      poSearchFieldProperties[fieldMapping.POSEARCH_CHINA_TOP_SIZE]
    ];
  }
  return [];
};

/**
 * Inserts the `poLine.itemVas` field after `poLine.itemVasDetail` in the
 * request fields when the detail field is present without it.
 * @param {Object} requestBody The request body with a `fields` array
 * @returns {Object} The request body with the item-VAS field added
 */
export const setFieldItemVas = (requestBody: Object) => {
  let updatedPayload = requestBody;
  if (requestBody?.fields?.includes('poLine.itemVasDetail')
        && !requestBody?.fields?.includes('poLine.itemVas')) {
    const fieldsData = [...requestBody.fields];
    const itemDetailsIndex = fieldsData.indexOf('poLine.itemVasDetail');
    fieldsData.splice(itemDetailsIndex + 1, 0, 'poLine.itemVas');
    updatedPayload = { ...requestBody, fields: fieldsData };
  }
  return updatedPayload;
};
/**
 * Converts single string values for multi-text search fields (product,
 * geography, style) into single-element arrays.
 * @param {Object} requestBody The request body with a `search` array
 * @returns {Object} The request body with multi-text values normalized
 */
export const convertSingleToMultiText = (requestBody: Object) => {
  const updatedPayload = { ...requestBody };
  if (Array.isArray(requestBody?.search)) {
    updatedPayload.search = requestBody.search.map((searchItem) => {
      if (
        (searchItem.fieldName === 'poLine.productCode'
        || searchItem.fieldName === 'poLine.geographyCode'
        || searchItem.fieldName === 'product.styleNumber')
      && typeof searchItem.fieldValue === 'string'
      ) {
        return {
          ...searchItem,
          fieldValue: [searchItem.fieldValue]
        };
      }
      return searchItem;
    });
  }
  return updatedPayload;
};

/**
 * Normalizes a saved-search response by converting single-to-multi-text
 * search values within each object's search criteria.
 * @param {Object} response The saved-search API response
 * @returns {Object} The normalized response
 */
export const normalizeSavedSearchResponse = (response) => {
  if (Array.isArray(response?.data?.objects)) {
    const updatedObjects = response.data.objects.map((obj) => {
      if (obj.searchCriteria) {
        return {
          ...obj,
          searchCriteria: convertSingleToMultiText(obj.searchCriteria)
        };
      }
      return obj;
    });
    return {
      ...response,
      data: {
        ...response.data,
        objects: updatedObjects
      }
    };
  }
  return response;
};

/**
 * Adds an "OR NOT IS_DEFINED" clause to the Asian-size-relevant filter when
 * it excludes "NO", so undefined values are also matched.
 * @param {Object} requestBody The request body with a `filter` array
 * @returns {Object} The request body with the adjusted filter
 */
export const setFilterAsiaSize = (requestBody: Object) => {
  let updatedPayload = { ...requestBody };
  if (requestBody?.filter) {
    const filters = [...requestBody.filter];
    const relevantFilterIndex = filters.findIndex(
      (filterItem) => filterItem.fieldName === 'poLine.asianSpecificSizeIsRelevant'
        && (
          filterItem.fieldValue?.includes('NO'))
    );
    if (relevantFilterIndex !== -1) {
      const relevantFilter = { ...filters[relevantFilterIndex] };
      relevantFilter.operator = 'OR';
      relevantFilter.function = 'NOT IS_DEFINED';
      filters[relevantFilterIndex] = relevantFilter;
      updatedPayload = { ...requestBody, filter: filters };
    }
  }
  return updatedPayload;
};

/**
 * Filters a payload's `fields` to those valid for the given field properties,
 * excluding feature-flag-inactive fields.
 * @param {Object} fieldProperties The field properties map
 * @param {Object} payload The request payload with a `fields` array
 * @param {Object} [dataTableHeaderInactv] Inactive table header rows
 * @param {Object} [inactiveFeatureFields] Feature-flag-hidden field primaries
 * @returns {Object} The payload with only valid fields
 */
export const setValidFields = (
  fieldProperties: Object,
  payload: Object,
  dataTableHeaderInactv: Object = [],
  inactiveFeatureFields: Object = []
) => {
  const { primaryFields, secondaryFields, additionalFields } = Object.values(fieldProperties)
    .reduce((acc, item) => {
      if (!inactiveFeatureFields.includes(item.primary)) {
        acc.primaryFields.push(item.primary);
        acc.secondaryFields.push(item.secondary);
        if (Array.isArray(item.additionalFields) && item.additionalFields.length > 0) {
          acc.additionalFields.push(item.additionalFields);
        }
      }
      return acc;
    }, {
      primaryFields: [],
      secondaryFields: [],
      additionalFields: []
    });
  const inactiveFields = dataTableHeaderInactv.length > 0 ? dataTableHeaderInactv?.map(
    (headRow: Object) => headRow.primary
  ) : [];
  const validFields = new Set(
    [
      ...primaryFields,
      ...secondaryFields,
      ...additionalFields.flat(),
      ...inactiveFields
    ].filter(Boolean)
  );
  const checkValid = payload?.fields.filter((item) => validFields.has(item));
  return {
    ...payload,
    fields: checkValid
  };
};

/**
 * Returns whether a page change crosses into a different 1000-row result set
 * (i.e. new data should be appended).
 * @param {number} currentPage The current page index
 * @param {number} newPage The target page index
 * @param {number} rowsPerPage The rows displayed per page
 * @returns {boolean} True when the result set index changes
 */
export function shouldAppendData(
  currentPage: number, newPage: number, rowsPerPage: number
): boolean {
  // Calculate the current set index
  const currentSetIndex = Math.floor(currentPage / (1000 / rowsPerPage));

  // Calculate the new set index
  const newSetIndex = Math.floor(newPage / (1000 / rowsPerPage));

  // Check if the set indexes are different
  return currentSetIndex !== newSetIndex;
}

/**
 * Relabels report header columns to their non-FPC equivalents when the user
 * lacks FPC access.
 * @param {Array<Object>} reportHeader The report header definitions
 * @param {boolean} hasAccess Whether the user has FPC access
 * @param {string} [pageName] Optional page context for label overrides
 * @returns {Array<Object>} The (possibly relabeled) report header
 */
export function reportHeaderWithFeatureFlag(reportHeader, hasAccess, pageName) {
  const headerLabelObj = {
    'Doc Number': pageName === 'afOverride' ? 'PO Number' : 'Purchase Order Number',
    'Line Item Number': 'PO Line Item Number',
    'Doc Date': 'Document Date',
    'Relation Type': 'Convert/Divert Indicator',
    'Doc Rejection': 'PO Rejection'
  };

  let updatedHeader = reportHeader;

  if (Array.isArray(reportHeader)) {
    updatedHeader = reportHeader.map((header) => {
      if (!hasAccess && headerLabelObj[header.label]) {
        return {
          ...header,
          label: headerLabelObj[header.label]
        };
      }
      return header;
    });
  } else if (!hasAccess && headerLabelObj[reportHeader] && typeof reportHeader === 'string') {
    updatedHeader = headerLabelObj[reportHeader];
  }
  return updatedHeader;
}

/**
 * Resolves the notification title for session timeout, profile creation, or a
 * generic error.
 * @param {Object} errorCodeText Error details (title and timeout flag)
 * @returns {string} The notification dialog title
 */
export function getNotificationTitle(errorCodeText) {
  const errTitle = errorCodeText?.errTitle === messageText.createProfileTitle
    ? errorCodeText?.errTitle
    : messageText.error;
  return errorCodeText?.errSessionTimeout ? messageText.sessionTimeout : errTitle;
}
